/**
 * Where a popup has to be rendered so it is actually on top.
 *
 * Both the select list and the date picker render into a portal rather than
 * inline, because every card in this application clips its corner flower with
 * `overflow: hidden` and a popup inside one is cut off at the card's edge.
 * `document.body` was the obvious destination and it is wrong for exactly one
 * case, which is now a common one.
 *
 * A `<dialog>` opened with `showModal()` is promoted to the browser's TOP
 * LAYER: it paints above the whole document, and above its own `::backdrop`,
 * regardless of z-index. Nothing in `document.body` can be drawn over it -
 * z-index is meaningless across that boundary. So a select inside a dialog
 * opened its list *behind* the dialog: invisible, and unclickable because the
 * backdrop swallowed the pointer. Every dropdown in the add-user, edit-member
 * and add-store dialogs was dead.
 *
 * The fix is to put the popup in the same layer as the thing that opened it:
 * the nearest open dialog if there is one, `document.body` otherwise. Nothing
 * else changes - the popup is still `position: fixed` in viewport coordinates,
 * and a dialog creates no containing block for it (no transform, no filter),
 * so it is not clipped by the dialog's own scrolling either.
 */
export function popupHost(trigger: Element | null): HTMLElement {
  const dialog = trigger?.closest('dialog');
  return dialog instanceof HTMLDialogElement && dialog.open ? dialog : document.body;
}

/**
 * How far a fixed popup inside `host` must be shifted to land on viewport
 * coordinates.
 *
 * Normally zero: `position: fixed` resolves against the viewport, so a rect
 * measured with `getBoundingClientRect()` can be used as-is. It stops being
 * zero the moment any ancestor establishes a containing block - a transform, a
 * filter, `contain`, `will-change` - because a fixed descendant then positions
 * against THAT element instead.
 *
 * This is not hypothetical. `.stagger > *` matched the dialog and gave it a
 * `fadeInUp` whose transform persists (`animation-fill-mode: both`), and every
 * dropdown inside a dialog landed one dialog-origin down and to the right. The
 * stylesheet no longer does that, and this makes the components correct whether
 * it does or not - the next transform added to a dialog should be a visual
 * decision, not a positioning bug.
 */
export function popupOrigin(host: HTMLElement): { left: number; top: number } {
  if (host === document.body) return { left: 0, top: 0 };

  const style = getComputedStyle(host);
  const establishes =
    style.transform !== 'none' ||
    style.filter !== 'none' ||
    style.perspective !== 'none' ||
    style.willChange.includes('transform') ||
    style.contain.includes('paint') ||
    style.contain.includes('layout');
  if (!establishes) return { left: 0, top: 0 };

  const box = host.getBoundingClientRect();
  return { left: box.left, top: box.top };
}
